// Modal-style picker that mirrors xExchange / Maiar pattern: a small dialog
// with the wallet options (extension, xPortal, web wallet, Ledger) and a
// "Detected" badge if the DeFi browser extension is installed.
//
// xPortal + Ledger flows render their UI (QR code, device check) inside an
// "anchor" HTML element managed by sdk-dapp's web components. We swap the
// picker buttons for that anchor when the user picks one of those providers.

import { useRef, useState } from "react";
import {
  connectExtension,
  connectLedger,
  connectWebWallet,
  connectXPortal,
  isExtensionDetected,
} from "./auth";

type Provider = "extension" | "xportal" | "web" | "ledger";

type Item = {
  key: Provider;
  label: string;
  hint: string;
  /** Some providers (xPortal/Ledger) mount their UI inside an anchor element.
   *  Extension + Web Wallet do their flow in their own surface and don't need one. */
  needsAnchor: boolean;
  connect: (anchor?: HTMLElement) => Promise<{ address: string } | null>;
  icon: JSX.Element;
};

const ITEMS: Item[] = [
  {
    key: "extension",
    label: "MultiversX DeFi Wallet",
    hint: "Browser extension (Chrome/Firefox)",
    needsAnchor: false,
    connect: () => connectExtension(),
    icon: <Icon emoji="🦊" />,
  },
  {
    key: "xportal",
    label: "xPortal App",
    hint: "Scan a QR code with your phone",
    needsAnchor: true,
    connect: (a) => connectXPortal(a),
    icon: <Icon emoji="📱" />,
  },
  {
    key: "web",
    label: "MultiversX Web Wallet",
    hint: "wallet.multiversx.com in a popup",
    needsAnchor: false,
    connect: () => connectWebWallet(),
    icon: <Icon emoji="🌐" />,
  },
  {
    key: "ledger",
    label: "Ledger",
    hint: "Hardware wallet via USB",
    needsAnchor: true,
    connect: (a) => connectLedger(a),
    icon: <Icon emoji="🔐" />,
  },
];

function Icon({ emoji }: { emoji: string }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "2rem",
        height: "2rem",
        background: "var(--bg-elev-2)",
        borderRadius: "0.5rem",
        fontSize: "1.1rem",
      }}
    >
      {emoji}
    </span>
  );
}

export default function WalletPicker({
  onClose,
  walletConnectEnabled,
}: {
  onClose: () => void;
  walletConnectEnabled: boolean;
}) {
  const [busy, setBusy] = useState<Provider | null>(null);
  const [active, setActive] = useState<Item | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const detected = isExtensionDetected();

  async function pick(it: Item) {
    setErr(null);
    setBusy(it.key);
    setActive(it.needsAnchor ? it : null);
    // For anchor-needing providers we have to let React commit the new DOM
    // (so the empty <div> exists) before sdk-dapp tries to mount inside it.
    // requestAnimationFrame waits one paint.
    const goConnect = async () => {
      try {
        const anchor = it.needsAnchor ? anchorRef.current ?? undefined : undefined;
        const r = await it.connect(anchor);
        if (!r) throw new Error("login cancelled");
        onClose();
      } catch (e) {
        setErr(String(e));
        setActive(null);
      } finally {
        setBusy(null);
      }
    };
    if (it.needsAnchor) {
      requestAnimationFrame(() => requestAnimationFrame(goConnect));
    } else {
      goConnect();
    }
  }

  function back() {
    setActive(null);
    setErr(null);
    setBusy(null);
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(2, 8, 18, 0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        backdropFilter: "blur(4px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{
          width: "100%",
          maxWidth: "26rem",
          padding: "1.25rem",
          border: "1px solid var(--border)",
        }}
      >
        <div
          className="row"
          style={{
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1rem",
          }}
        >
          <h3 style={{ margin: 0 }}>
            {active ? `Connect via ${active.label}` : "Connect a wallet"}
          </h3>
          <div className="row" style={{ gap: "0.3rem" }}>
            {active && (
              <button
                className="ghost"
                onClick={back}
                aria-label="back"
                style={{ padding: "0.15rem 0.55rem" }}
              >
                ←
              </button>
            )}
            <button
              className="ghost"
              onClick={onClose}
              aria-label="close"
              style={{ padding: "0.15rem 0.55rem" }}
            >
              ×
            </button>
          </div>
        </div>

        {active ? (
          // sdk-dapp's <mvx-wallet-connect> / Ledger web component mounts here.
          <div
            ref={anchorRef}
            style={{ minHeight: "16rem", display: "flex", justifyContent: "center" }}
          />
        ) : (
          <div className="col" style={{ gap: "0.5rem" }}>
            {ITEMS
              .filter((it) => it.key !== "xportal" || walletConnectEnabled)
              .map((it) => {
                const disabled = busy !== null;
                const isDetected = it.key === "extension" && detected;
                const isBusy = busy === it.key;
                return (
                  <button
                    key={it.key}
                    className="ghost"
                    disabled={disabled}
                    onClick={() => pick(it)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      padding: "0.7rem 0.9rem",
                      textAlign: "left",
                      width: "100%",
                      justifyContent: "flex-start",
                      opacity: disabled && !isBusy ? 0.55 : 1,
                      cursor: disabled && !isBusy ? "not-allowed" : "pointer",
                    }}
                  >
                    {it.icon}
                    <div className="col" style={{ gap: "0.1rem", flex: 1 }}>
                      <span style={{ fontWeight: 500 }}>
                        {it.label}
                        {isDetected && (
                          <span className="badge live" style={{ marginLeft: "0.5rem" }}>
                            detected
                          </span>
                        )}
                      </span>
                      <span className="muted" style={{ fontSize: "0.8rem" }}>
                        {it.hint}
                      </span>
                    </div>
                    {isBusy && <span className="muted">…</span>}
                  </button>
                );
              })}
          </div>
        )}

        {err && (
          <p
            className="mono"
            style={{ color: "var(--danger)", fontSize: "0.85rem", marginTop: "0.8rem" }}
          >
            {err}
          </p>
        )}
      </div>
    </div>
  );
}
